import { useEffect, useState } from "react";

/**
 * A value that lags behind, for search-as-you-type.
 *
 * Typing a registration number is ten keystrokes. Without this it is ten
 * requests, and because they return independently the answers can arrive out
 * of order — so the list can settle on the results for `KA51A` while the box
 * reads `KA51AN1511`. Debouncing the *query* fixes that at the source, which is
 * better than cancelling responses after the fact.
 *
 * 300ms is the usual floor: long enough to swallow a burst of typing, short
 * enough that pausing feels like it already searched.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    // Clearing on every change is what makes this a debounce rather than a
    // throttle: a keystroke inside the window restarts the clock.
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
